import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AgendaService } from '../../../../services/business';
import { EventForReports, GetEventsForReportsParams } from '../../../../shared/interfaces';
import { AnalyticsRequest } from '../../../../shared/interfaces/analytics.interface';
import { eventPrice, formatCurrency, formatNumber, professionalLabel, serviceLabel, statusMeta } from '../../utils/report-format';

interface Row {
  id: string;
  day: string;
  time: string;
  client: string;
  service: string;
  professional: string;
  statusLabel: string;
  statusColor: string;
  price: string;
  paid: boolean;
}

/** Detalle de las citas del período con los mismos filtros del reporte. */
@Component({
  selector: 'app-appointments-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './appointments-table.html',
})
export class AppointmentsTable implements OnChanges {
  @Input({ required: true }) request!: AnalyticsRequest;
  @Input() showProfessional = true;

  private readonly agendaService = inject(AgendaService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private sub?: Subscription;

  readonly pageSize = 15;
  page = 1;
  totalCount = 0;
  rows: Row[] = [];
  loading = false;
  error = false;

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get rangeText(): string {
    if (!this.totalCount) return '';
    const from = (this.page - 1) * this.pageSize + 1;
    const to = Math.min(this.page * this.pageSize, this.totalCount);
    return `${formatNumber(from)}–${formatNumber(to)} de ${formatNumber(this.totalCount)}`;
  }

  ngOnChanges(): void {
    this.page = 1;
    this.load();
  }

  goTo(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) return;
    this.page = page;
    this.load();
  }

  load(): void {
    if (!this.request) return;
    this.sub?.unsubscribe();
    this.loading = true;
    this.error = false;

    const params: GetEventsForReportsParams = {
      startDate: this.request.startDate,
      endDate: this.request.endDate,
      professionalId: this.request.professionalId,
      serviceId: this.request.serviceId,
      clientId: this.request.clientId,
      statusCode: this.request.statusCode,
      page: this.page,
      pageSize: this.pageSize,
      sortBy: 'startDate',
      sortOrder: 'desc',
    };

    this.sub = this.agendaService
      .getEventsForReports(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.rows = (res.events ?? []).map((e) => this.toRow(e));
          this.totalCount = res.totalCount ?? 0;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.error = true;
          this.cdr.markForCheck();
        },
      });
  }

  private toRow(e: EventForReports): Row {
    const start = new Date(e.startDate);
    const meta = statusMeta(e.statusCode, e.statusName);
    const price = eventPrice(e);
    return {
      id: e.eventId,
      day: format(start, "EEE d MMM yyyy", { locale: es }),
      time: format(start, 'h:mm a', { locale: es }),
      client: e.clientName?.trim() || 'Sin nombre',
      service: serviceLabel(e),
      professional: professionalLabel(e),
      statusLabel: e.statusName || meta.label,
      statusColor: meta.color,
      price: price > 0 ? formatCurrency(price) : '—',
      paid: e.paymentCompleted,
    };
  }
}
